import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Map } from "lucide-react";
import { cn } from "@nexus/ui";
import type { PreflightMarketKind } from "@nexus/contracts";

export interface MarketIconProps {
  kind: PreflightMarketKind;
  /** Catalogue code — names the flag file for a country. */
  code: string;
  /** Catalogue name, used for the accessible label. */
  name: string;
  className?: string;
}

/**
 * A market's catalogue icon (R12): the country's flag, a globe for Global, a
 * region glyph for a continent.
 *
 * Flags are served as files from `public/flags`, so one that fails to load
 * degrades to the region glyph instead of a broken image.
 */
export function MarketIcon({ kind, code, name, className }: MarketIconProps) {
  const { t } = useTranslation();
  const [failedCode, setFailedCode] = useState<string | null>(null);

  const glyphClassName = cn("size-3.5 shrink-0", className);
  const glyphLabel = t("preflight.markets.iconAlt", "{{market}} icon", {
    market: name,
  });

  if (kind === "GLOBAL") {
    return <Globe role="img" aria-label={glyphLabel} className={glyphClassName} />;
  }

  if (kind === "COUNTRY" && failedCode !== code) {
    return (
      <img
        src={`/flags/${code}.svg`}
        alt={t("preflight.markets.flagAlt", "{{market}} flag", { market: name })}
        width={18}
        height={12}
        loading="lazy"
        onError={() => setFailedCode(code)}
        className={cn(
          "h-3 w-[1.125rem] shrink-0 rounded-[2px] object-cover",
          className,
        )}
      />
    );
  }

  return <Map role="img" aria-label={glyphLabel} className={glyphClassName} />;
}
